/**
 * The stuck state of sticky bars where CSS can't tell it (THEME.md §7.11, §11.2; T-204).
 * A sticky element that looks different while it's stuck, such as the collection filter row with its frosted glass,
 * is a scroll-state container and styles that look in `@container scroll-state(stuck: top)`. Browsers without
 * scroll-state queries (Safari and Firefox in 2026) get `data-okt-stuck` on it instead, styled the same, while it
 * rests on its `top` edge of the page's scroller (or has passed it as its container leaves) rather than lower in its
 * place. Another sticky element joins by adding its selector to STICKY and styling `[data-okt-stuck]` beside its query.
 * Loaded on every page by snippets/okt-tokens.liquid; it does nothing where the browser has the queries or the page
 * has no such element.
 */
import { getScrollContainer, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const STICKY = '.collection-wrapper > :is(.facets-toggle, .facets-block-wrapper--horizontal)';
const STUCK = 'data-okt-stuck';

/**
 * Shown, sticky with a `top`, and at or above that edge of the page's scroller: `.page-wrapper` from 990 px, the
 * viewport below.
 * @param {Element} element
 */
function isStuck(element) {
  const style = getComputedStyle(element);
  const top = parseFloat(style.top);
  if (style.position !== 'sticky' || !Number.isFinite(top) || !element.getClientRects().length) return false;
  const scroller = getScrollContainer();
  const edge = scroller === document.scrollingElement ? 0 : scroller.getBoundingClientRect().top + scroller.clientTop;
  return element.getBoundingClientRect().top - edge <= top + 0.5;
}

let frame = 0;

/** @type {EventTarget | undefined} */
let scrollTarget;

/** Horizon's morph drops the attribute when it renders the section again (filters, sorting): put it back at once */
const keeper = new MutationObserver(update);

function update() {
  cancelAnimationFrame(frame);
  frame = 0;
  for (const element of document.querySelectorAll(STICKY)) element.toggleAttribute(STUCK, isStuck(element));
  keeper.takeRecords();
}

function queue() {
  if (!frame) frame = requestAnimationFrame(update);
}

/** The scroller changes at 990 px */
function listen() {
  scrollTarget?.removeEventListener('scroll', queue);
  scrollTarget = getScrollEventTarget();
  scrollTarget.addEventListener('scroll', queue, { passive: true });
  queue();
}

/**
 * The header offset moves the `top` edge (THEME.md §7.1), with the header: measured again once the move has ended
 * @param {TransitionEvent} event
 */
function handleTransition(event) {
  if (event.propertyName === 'top') queue();
}

if (!CSS.supports('container-type', 'scroll-state') && document.querySelector(STICKY)) {
  keeper.observe(document.body, { subtree: true, attributeFilter: [STUCK] });
  listen();
  scrollContainerMediaQuery.addEventListener('change', listen);
  window.addEventListener('resize', queue, { passive: true });
  document.addEventListener('transitionend', handleTransition);
  document.addEventListener('transitioncancel', handleTransition);
  // The theme editor renders a section again on every change
  document.addEventListener('shopify:section:load', queue);
  update();
}
